import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useData } from '../providers/DataProvider';
import { Building2, Users, GitBranch, Plus, Settings, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Businesses management page (Admin only)
 * Shows list of all businesses with member counts and allows navigation to details
 */
const Businesses = () => {
  const { currentUser } = useData();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBusiness, setNewBusiness] = useState({ code: '', name: '' });
  const [creating, setCreating] = useState(false);

  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchBusinesses();
    }
  }, [isAdmin]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch businesses with member count and branch count
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select(`
          id,
          code,
          name,
          is_active,
          settings,
          created_at,
          business_memberships(count),
          branches(count)
        `)
        .order('created_at', { ascending: true });

      if (businessError) throw businessError;

      // Transform data to include counts
      const transformedData = (businessData || []).map(b => ({
        ...b,
        memberCount: b.business_memberships?.[0]?.count || 0,
        branchCount: b.branches?.[0]?.count || 0,
      }));

      setBusinesses(transformedData);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    if (!newBusiness.code || !newBusiness.name) return;

    try {
      setCreating(true);
      
      const { data, error } = await supabase
        .from('businesses')
        .insert([{
          code: newBusiness.code.toUpperCase().replace(/\s+/g, '_'),
          name: newBusiness.name,
          is_active: true,
        }])
        .select()
        .single();

      if (error) throw error;

      setBusinesses(prev => [...prev, { ...data, memberCount: 0, branchCount: 0 }]);
      setNewBusiness({ code: '', name: '' });
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating business:', err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500 dark:text-gray-400">
        <Building2 className="w-16 h-16 mb-4 opacity-50" />
        <h2 className="text-xl font-semibold mb-2">Acceso Restringido</h2>
        <p>Solo los administradores pueden ver esta sección.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Empresas</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Gestiona las empresas y sus miembros
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva Empresa
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Business Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {businesses.map((business) => (
          <div
            key={business.id}
            className={cn(
              'bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6',
              'hover:shadow-lg transition-all duration-200 cursor-pointer group'
            )}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  business.is_active 
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                )}>
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    {business.name}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {business.code}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Users className="w-4 h-4 text-gray-400" />
                <span>{business.memberCount} miembros</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <GitBranch className="w-4 h-4 text-gray-400" />
                <span>{business.branchCount} sucursales</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs">
                <span className={cn(
                  'px-2 py-1 rounded-full',
                  business.is_active
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                )}>
                  {business.is_active ? 'Activo' : 'Inactivo'}
                </span>
                <span className="text-gray-400">
                  {new Date(business.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}

        {businesses.length === 0 && !loading && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Building2 className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay empresas registradas</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Crear primera empresa
            </button>
          </div>
        )}
      </div>

      {/* Create Business Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Nueva Empresa
            </h2>
            <form onSubmit={handleCreateBusiness} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Código
                </label>
                <input
                  type="text"
                  value={newBusiness.code}
                  onChange={(e) => setNewBusiness(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="EMPRESA_CODE"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Identificador único (sin espacios, mayúsculas)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={newBusiness.name}
                  onChange={(e) => setNewBusiness(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre de la empresa"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {creating ? 'Creando...' : 'Crear Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Businesses;
